import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from './components.js';

/**
 * Data-visualization components. Marks follow the dataviz method: thin marks, 2px lines,
 * 4px rounded data-ends, hairline solid grid, values on hover/focus with a table view as the
 * non-hover equivalent. Colors come only from --viz-* tokens.
 */

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/** Clean axis ticks (1, 2, 5 × 10^k) from 0 to at least max. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.0001; v += step) ticks.push(Number(v.toFixed(10)));
  if (ticks[ticks.length - 1]! < max) ticks.push(ticks[ticks.length - 1]! + step);
  return ticks;
}

export function compactNumber(v: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
    v,
  );
}

// ── Chart card with table-view toggle ─────────────────────────────────────

export function ChartCard({
  title,
  subtitle,
  headline,
  table,
  className,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  /** A headline figure shown under the title (e.g. the period total). */
  headline?: ReactNode;
  /** Accessible table equivalent of the chart. */
  table?: { columns: string[]; rows: (string | number)[][] };
  className?: string;
  children: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  return (
    <figure className={cx('card chart-card', className)}>
      <figcaption className="chart-card__header">
        <div>
          <div className="chart-card__title">{title}</div>
          {subtitle && <div className="chart-card__subtitle">{subtitle}</div>}
          {headline && <div className="chart-card__headline">{headline}</div>}
        </div>
        {table && (
          <button
            type="button"
            className="chart-card__toggle"
            onClick={() => setShowTable((v) => !v)}
            aria-pressed={showTable}
          >
            {showTable ? 'Chart' : 'Table'}
          </button>
        )}
      </figcaption>
      <div className="chart-card__body">
        {showTable && table ? (
          <div className="table-wrap chart-card__table">
            <table className="table">
              <thead>
                <tr>
                  {table.columns.map((c, i) => (
                    <th key={c} className={i > 0 ? 'table__actions' : undefined}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j} className={j > 0 ? 'table__actions num' : undefined}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </figure>
  );
}

// ── Time series (area or column), single series ───────────────────────────

export interface TimePoint {
  key: string;
  value: number;
}

const PAD = { top: 12, right: 12, bottom: 28, left: 44 };

export function TimeSeriesChart({
  data,
  kind,
  height = 220,
  formatValue,
  formatTick = compactNumber,
  formatKey,
  ariaLabel,
  emptyMessage = 'No data in this period',
}: {
  data: TimePoint[];
  kind: 'area' | 'column';
  height?: number;
  formatValue: (v: number) => string;
  formatTick?: (v: number) => string;
  formatKey: (key: string) => string;
  ariaLabel: string;
  emptyMessage?: string;
}) {
  const { ref, width } = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const tipId = useId();
  const tipRef = useRef<HTMLDivElement>(null);
  const n = data.length;
  const max = Math.max(0, ...data.map((d) => d.value));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const band = n > 0 ? plotW / n : 0;
  const x = (i: number) =>
    PAD.left +
    (kind === 'column' ? band * i + band / 2 : n > 1 ? (plotW * i) / (n - 1) : plotW / 2);
  const y = (v: number) => PAD.top + plotH - (top > 0 ? (v / top) * plotH : 0);
  const baseline = PAD.top + plotH;
  const empty = max === 0;

  const pick = useCallback(
    (clientX: number, rect: DOMRect) => {
      if (n === 0) return;
      const px = clientX - rect.left - PAD.left;
      const i = kind === 'column' ? Math.floor(px / band) : Math.round((px / plotW) * (n - 1));
      setActive(Math.min(n - 1, Math.max(0, i)));
    },
    [band, kind, n, plotW],
  );

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') setActive((a) => Math.min(n - 1, (a ?? -1) + 1));
    else if (e.key === 'ArrowLeft') setActive((a) => Math.max(0, (a ?? n) - 1));
    else if (e.key === 'Escape') setActive(null);
    else return;
    e.preventDefault();
  };

  // The tooltip follows the hovered point; its position is passed to CSS as a variable.
  const tipX = active === null ? 0 : Math.min(Math.max(x(active), 70), width - 70);
  useLayoutEffect(() => {
    tipRef.current?.style.setProperty('--tip-x', `${tipX}px`);
  }, [tipX, active]);

  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 72))));
  const linePath = data
    .map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`)
    .join(' ');
  const barW = Math.max(2, Math.min(24, band - 2));
  const r = Math.min(4, barW / 2);

  return (
    <div className="ts-chart" ref={ref}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          aria-describedby={active !== null ? tipId : undefined}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
          onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
          onPointerLeave={() => setActive(null)}
          className="ts-chart__svg"
        >
          {ticks.map((t) => (
            <g key={t}>
              <line className="viz-grid" x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} />
              <text className="viz-tick" x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {formatTick(t)}
              </text>
            </g>
          ))}
          <line
            className="viz-axis"
            x1={PAD.left}
            x2={width - PAD.right}
            y1={baseline}
            y2={baseline}
          />
          {data.map((d, i) =>
            (i % labelEvery === 0 && n - 1 - i >= labelEvery * 0.7) || i === n - 1 ? (
              <text
                key={d.key}
                className="viz-tick"
                x={x(i)}
                y={height - 8}
                textAnchor={
                  i === 0 && kind === 'area'
                    ? 'start'
                    : i === n - 1 && kind === 'area'
                      ? 'end'
                      : 'middle'
                }
              >
                {formatKey(d.key)}
              </text>
            ) : null,
          )}

          {kind === 'area' && !empty && (
            <>
              <path
                className="viz-area"
                d={`${linePath} L${x(n - 1).toFixed(1)},${baseline} L${x(0).toFixed(1)},${baseline} Z`}
              />
              <path className="viz-line" d={linePath} />
              <circle className="viz-dot" cx={x(n - 1)} cy={y(data[n - 1]!.value)} r={4} />
            </>
          )}
          {kind === 'column' &&
            data.map((d, i) => {
              if (d.value <= 0) return null;
              const h = baseline - y(d.value);
              const left = x(i) - barW / 2;
              const rr = Math.min(r, h);
              return (
                <path
                  key={d.key}
                  className={cx('viz-bar', active === i && 'viz-bar--active')}
                  d={`M${left},${baseline} V${baseline - h + rr} Q${left},${baseline - h} ${left + rr},${baseline - h} H${left + barW - rr} Q${left + barW},${baseline - h} ${left + barW},${baseline - h + rr} V${baseline} Z`}
                />
              );
            })}

          {active !== null && kind === 'area' && (
            <>
              <line
                className="viz-crosshair"
                x1={x(active)}
                x2={x(active)}
                y1={PAD.top}
                y2={baseline}
              />
              <circle className="viz-dot" cx={x(active)} cy={y(data[active]!.value)} r={4} />
            </>
          )}
        </svg>
      )}
      {empty && width > 0 && <div className="ts-chart__empty">{emptyMessage}</div>}
      {active !== null && width > 0 && data[active] && (
        <div id={tipId} role="status" className="viz-tooltip" ref={tipRef}>
          <span className="viz-tooltip__value">{formatValue(data[active].value)}</span>
          <span className="viz-tooltip__label">{formatKey(data[active].key)}</span>
        </div>
      )}
    </div>
  );
}

// ── Sparkline ─────────────────────────────────────────────────────────────

export function Sparkline({ values, height = 32 }: { values: number[]; height?: number }) {
  if (values.length < 2) return null;
  const w = 100;
  const max = Math.max(...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const px = (i: number) => (w * i) / (values.length - 1);
  const py = (v: number) => height - 3 - ((v - min) / span) * (height - 6);
  const d = values
    .map((v, i) => `${i ? 'L' : 'M'}${px(i).toFixed(2)},${py(v).toFixed(2)}`)
    .join(' ');
  // Stretches to the tile width; the stroke keeps its 2px width while scaling.
  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      height={height}
      aria-hidden="true"
    >
      <path className="sparkline__line" d={d} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── Stat tile ─────────────────────────────────────────────────────────────

export function StatTile({
  label,
  value,
  delta,
  comparison = 'vs previous period',
  trend,
  icon,
  hint,
}: {
  label: string;
  value: ReactNode;
  /** Relative change; null when there is no previous value to compare with. */
  delta?: { change: number | null; upIsGood?: boolean };
  comparison?: string;
  trend?: number[];
  icon?: ReactNode;
  hint?: ReactNode;
}) {
  const change = delta?.change ?? null;
  const good = change === null || change === 0 ? null : change > 0 === (delta?.upIsGood ?? true);
  return (
    <div className="card stat-tile">
      <div className="stat-tile__label">
        {icon && <span className="kpi__icon">{icon}</span>}
        {label}
      </div>
      <div className="stat-tile__value">{value}</div>
      {delta && (
        <div className="stat-tile__delta">
          {change === null ? (
            <span className="text-muted">No data for the previous period</span>
          ) : (
            <>
              <span
                className={cx(
                  'delta',
                  good === true && 'delta--good',
                  good === false && 'delta--bad',
                )}
              >
                <span aria-hidden="true">{change > 0 ? '▲' : change < 0 ? '▼' : '■'}</span>{' '}
                {change === 0
                  ? 'No change'
                  : `${Math.abs(change * 100) >= 1000 ? '>999' : Math.abs(change * 100).toFixed(0)}%`}
                <span className="visually-hidden">
                  {change > 0 ? ' increase' : change < 0 ? ' decrease' : ''}
                </span>
              </span>
              <span className="text-muted"> {comparison}</span>
            </>
          )}
        </div>
      )}
      {trend && <Sparkline values={trend} />}
      {hint && <div className="stat-tile__hint">{hint}</div>}
    </div>
  );
}

// ── Horizontal bar list (one series) ──────────────────────────────────────

/** Bar from the baseline (x=0): square start, 4px rounded data-end. */
function hbarPath(w: number, h: number): string {
  const r = Math.min(4, h / 2, w);
  if (w <= 0) return '';
  return `M0,0 H${w - r} Q${w},0 ${w},${r} V${h - r} Q${w},${h} ${w - r},${h} H0 Z`;
}

export function BarList({
  items,
  emptyMessage = 'No data in this period',
}: {
  items: { label: string; value: number; display: string; note?: string }[];
  emptyMessage?: string;
}) {
  const { ref, width } = useWidth<HTMLUListElement>();
  const max = Math.max(0, ...items.map((i) => i.value));
  if (max === 0) return <div className="chart-empty">{emptyMessage}</div>;
  const barH = 8;
  return (
    <ul className="bar-list" ref={ref}>
      {items.map((i) => (
        <li key={i.label} className="bar-list__item" title={`${i.label}: ${i.display}`}>
          <div className="bar-list__labels">
            <span className="bar-list__label">{i.label}</span>
            <span className="bar-list__value">
              {i.display}
              {i.note && <span className="text-muted"> · {i.note}</span>}
            </span>
          </div>
          {width > 0 && (
            <svg className="bar-list__svg" width={width} height={barH} aria-hidden="true">
              <rect
                className="bar-list__track"
                x={0}
                y={0}
                width={width}
                height={barH}
                rx={barH / 2}
              />
              {i.value > 0 && (
                <path
                  className="viz-bar"
                  d={hbarPath(Math.max(2, (i.value / max) * width), barH)}
                />
              )}
            </svg>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── 100% stacked bar (part-to-whole, ≤ 3 categorical slots) ──────────────

export function StackedBar({
  segments,
  emptyMessage = 'No data in this period',
}: {
  segments: { label: string; value: number; display: string; slot: 1 | 2 | 3 }[];
  emptyMessage?: string;
}) {
  const { ref, width } = useWidth<HTMLDivElement>();
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total === 0) return <div className="chart-empty">{emptyMessage}</div>;
  const visible = segments.filter((s) => s.value > 0);
  const gap = 2;
  const barH = 20;
  const usable = Math.max(0, width - gap * (visible.length - 1));
  const widths = visible.map((s) => Math.max(1, (s.value / total) * usable));
  const starts = widths.map((_, i) => widths.slice(0, i).reduce((a, w) => a + w + gap, 0));
  const rects = visible.map((s, i) => {
    const w = widths[i]!;
    const x = starts[i]!;
    const last = i === visible.length - 1;
    const r = last ? Math.min(4, w) : 0;
    // Only the far end (the data-end) is rounded; the baseline end stays square.
    const d = `M${x},0 H${x + w - r} Q${x + w},0 ${x + w},${r} V${barH - r} Q${x + w},${barH} ${x + w - r},${barH} H${x} Z`;
    return { ...s, d };
  });
  return (
    <div className="stacked" ref={ref}>
      {width > 0 && (
        <svg
          className="stacked__svg"
          width={width}
          height={barH}
          role="img"
          aria-label={segments.map((s) => `${s.label} ${s.display}`).join(', ')}
        >
          {rects.map((s) => (
            <path key={s.label} className={`stacked__seg stacked__seg--${s.slot}`} d={s.d}>
              <title>{`${s.label}: ${s.display}`}</title>
            </path>
          ))}
        </svg>
      )}
      {/* Legend with values: identity never relies on color alone. */}
      <ul className="stacked__legend">
        {segments.map((s) => (
          <li key={s.label} className="stacked__legend-item">
            <svg className="stacked__swatch" width={10} height={10} aria-hidden="true">
              <rect className={`stacked__seg--${s.slot}`} width={10} height={10} rx={2} />
            </svg>
            <span className="stacked__legend-label">{s.label}</span>
            <span className="stacked__legend-value">{s.display}</span>
            <span className="text-muted stacked__legend-share">
              {((s.value / total) * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
